import { useEffect, useState, type FormEvent } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "./ui/dialog";
import { Button } from "./ui/button";
import { requestMagicLink, signOut } from "../lib/auth-api";

interface SignInDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  redirectUrl: string;
}

export function SignInDialog({ open, onOpenChange, redirectUrl }: SignInDialogProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setStatus("idle");
      setMessage(null);
    }
  }, [open]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("sending");
    setMessage(null);
    try {
      const result = await requestMagicLink({ email, redirectUrl });
      setStatus("sent");
      setMessage(result.message);
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "Unable to send sign-in link");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Sign in to TrackInsiders</DialogTitle>
          <DialogDescription>
            We email a one-time link. No password and no Cloudflare Access seat.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-3" onSubmit={onSubmit}>
          <label className="block text-xs font-medium text-muted-foreground" htmlFor="signin-email">
            Work or personal email
          </label>
          <input
            id="signin-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none ring-accent focus-visible:ring-2"
            placeholder="you@firm.com"
          />
          {message ? (
            <p
              className={`text-xs leading-5 ${status === "error" ? "text-destructive" : "text-muted-foreground"}`}
              role="status"
            >
              {message}
            </p>
          ) : (
            <p className="text-xs leading-5 text-muted-foreground">
              Favorites, saved views, and alerts stay tied to this email.
            </p>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={status === "sending" || status === "sent"}>
              {status === "sending" ? "Sending…" : status === "sent" ? "Link sent" : "Email me a link"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export async function performSignOut(): Promise<void> {
  await signOut();
}
